/* ============================================================
   API client — thin wrapper over fetch for backend endpoints

   - Attaches Authorization: Bearer <token> when a session exists.
   - On 401 with a stored refresh token: refreshes once and
     retries the original request a single time.
   - Anonymous requests (no stored session) are sent as-is, so
     the backend staging-guest fallback keeps working.
   ============================================================ */

import type {
  ApiResult,
  Material,
  MakerWorldPrefillResultDto,
  ModelFileResponse,
  OrderChatMessage,
  OrderChatThread,
  PerformerMatches,
  QuoteRequest,
  QuoteResponse,
} from '../types';
import type {
  AuthResponse,
  LoginRequest,
  RegisterRequest,
  Profile,
  UpdateProfileRequest,
  ChangePasswordRequest,
} from '../types/auth';
import type { PublicPickupPoint, ProductReview, ProductReviewsPage, RepeatOrderResult, SafeDealPaymentInitiation } from '../types/product';
import type {
  CarrierPointSelection,
  DeliveryPointCities,
  DeliveryPointPage,
  IpCityResult,
  OzonPointAvailability,
  OzonQuoteInput,
  OzonQuoteResult,
} from '../types/delivery';
import type { NotificationList } from '../types/notification';
import type {
  PrinterSlicingProfiles,
  SlicingContext,
  SlicingJob,
} from '../types/slicing';
import type { NalogChallengeStarted, NalogConnection, NalogReceipt } from '../types/nalog';
import type { BambuConnection, BambuPrinter } from '../types/bambu';
import type { BambuPrintJob } from '../types/printing';
import type { FavoriteIds, FavoriteList, FavoriteToggleResult } from '../types/favorite';
import type {
  ProductCategoryInput,
  ProductCategoryNode,
  ProductPage,
  ProductPageQuery,
} from '../types/category';
import type {
  AddressBookEntry,
  AddressSuggestion,
  CreateAddressRequest,
  UpdateAddressRequest,
} from '../types/address';
import type { AdminUser, AdminUserPage, AdminOrderPage, AdminDisputePage, PlatformSettings, AdminDispute, AdminOrder, DisputeOutcome, PaymentsConfigStatus, ProviderCommission } from '../types/admin';
import type {
  ProviderReferral,
  ReferralAttributionRow,
  ReferralLedgerEntry,
  ReferralProgramSettings,
  ReferralResolve,
} from '../types/referral';
import { getAccessToken, getRefreshToken, saveAuth, clearAuth } from '../auth/tokenStore';
import type {
  ProviderProfileDetail,
  RegisterProvider,
  UpdateProviderProfile,
  PrinterDetail,
  CreatePrinter,
  UpdatePrinter,
  ProviderMaterial,
  AddProviderMaterial,
  MediaUploadResponse,
  UploadedMedia,
  ProviderOrder,
  ProviderOrderPage,
  ProviderCatalogBrowseResult,
  ProviderCatalogDetail,
  PrinterModelCatalogResponse,
  ProviderPayoutMethod,
  ProviderPayoutWidgetConfig,
  UpdateProviderPayoutMethod,
  DeliverySettings,
  UpdateDeliverySettings,
  MaterialStockRow,
  MaterialStockItem,
  ProviderEarnings,
} from '../types/provider';
import type {
  ProductSummary,
  ProductDetail,
  ProductDraft,
  ProductMinePage,
  Cart,
  OrderSummary,
  OrderSummaryPage,
  OrderDetail,
  CounterOffer,
  WebOrderStatus,
} from '../types/product';
import { orderStatusToJson } from '../types/product';

/**
 * 0258c2c7: printers wire mapper. The API binds enums as ordinal numbers only
 * (AddControllers default JSON, no JsonStringEnumConverter — verified: string
 * "Fdm" fails binding with 400). Map the canonical string unions used inside
 * the web app to ordinals at the fetch boundary, mirroring the ingest-side
 * normalizePrinterTechnology / normalizeSlicerEngine defensive normalizers.
 */
const TECH_ORDINALS: Record<string, number> = { Fdm: 0, Sla: 1, Sls: 2 };

function printerWireDto<T extends object>(dto: T) {
  const wire = { ...dto } as Record<string, unknown>;
  // UpdatePrinter carries no technology — only remap what the DTO actually has.
  if ('technology' in dto) wire.technology = TECH_ORDINALS[String(dto.technology)] ?? 0;
  if ('slicerEngine' in dto) wire.slicerEngine = dto.slicerEngine === 'Cura' || dto.slicerEngine === 1 ? 1 : 0;
  return wire;
}
import type {
  ProviderPricingSettings,
  SellerQuotePreview,
  SellerQuotePreviewRequest,
  Review,
} from '../types/pricing';

const BASE_URL = `${import.meta.env.VITE_API_BASE ?? ''}/api/v1`;

/** TASK-241: origin serving /media — derive it from the API base so
 *  relative /media/… photo URLs resolve against the API host in dev. */
export function resolveMediaUrl(url: string): string {
  if (!url.startsWith('/')) return url;
  try {
    return new URL(url, new URL(BASE_URL, window.location.origin).origin).href;
  } catch {
    return url;
  }
}

/** Formats a RFC7807 ProblemDetails body (incl. validation errors) into a readable line. */
function formatProblemDetails(body: Record<string, unknown>): string {
  const parts: string[] = [];
  if (typeof body.title === 'string' && body.title) parts.push(body.title);
  if (body.errors && typeof body.errors === 'object') {
    for (const messages of Object.values(body.errors as Record<string, string[]>)) {
      if (Array.isArray(messages)) parts.push(...messages.filter((m) => typeof m === 'string'));
    }
  }
  return parts.join(' ') || 'Ошибка валидации';
}

async function handleResponse<T>(res: Response): Promise<T> {
  // TASK-196: never call res.json() blindly — empty or non-JSON bodies
  // (proxy 500s, exception pages) used to throw an opaque
  // "Unexpected end of JSON input" instead of a readable error.
  const text = await res.text();

  let body: unknown = null;
  if (text.length > 0) {
    try {
      body = JSON.parse(text);
    } catch {
      // Non-JSON body — handled below.
    }
  }

  if (!res.ok) {
    if (body && typeof body === 'object' && 'error' in body) {
      const err = (body as { error?: unknown }).error;
      if (typeof err === 'string' && err) throw new Error(err);
    }
    if (body && typeof body === 'object' && 'title' in body) {
      throw new Error(formatProblemDetails(body as Record<string, unknown>));
    }
    if (text.length > 0 && typeof body !== 'object') {
      throw new Error(`Сервер вернул ошибку (HTTP ${res.status})`);
    }
    throw new Error(`Сервер вернул ошибку (HTTP ${res.status}, пустой ответ). Проверьте подключение к API.`);
  }

  if (text.length === 0) {
    throw new Error(`Пустой ответ сервера (HTTP ${res.status}).`);
  }

  if (body === null || typeof body !== 'object') {
    throw new Error(`Некорректный ответ сервера (HTTP ${res.status}).`);
  }

  // If wrapped in ApiResult
  if ('isSuccess' in body) {
    const result = body as ApiResult<T>;
    if (!result.isSuccess) {
      throw new Error(result.error ?? 'Неизвестная ошибка');
    }
    return result.value as T;
  }

  // Direct return (e.g. GET /materials returns array directly)
  return body as T;
}

/* ── Token refresh (single-flight) ─────────────────────── */

let refreshInFlight: Promise<AuthResponse | null> | null = null;

async function refreshTokens(): Promise<AuthResponse | null> {
  if (!refreshInFlight) {
    refreshInFlight = (async () => {
      const refreshToken = getRefreshToken();
      if (!refreshToken) return null;
      try {
        const res = await fetch(`${BASE_URL}/auth/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refreshToken }),
        });
        const body = (await res.json()) as ApiResult<AuthResponse>;
        if (!res.ok || !body.isSuccess || !body.value) {
          // Refresh token revoked/expired — drop the session; user can re-login.
          clearAuth();
          return null;
        }
        return saveAuth(body.value);
      } catch {
        // Network error — keep the current session; the caller surfaces the 401.
        return null;
      }
    })();
    // Allow subsequent 401s to start a new refresh cycle once this one settles.
    void refreshInFlight.finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

/* ── Core request helper ───────────────────────────────── */

interface RequestOptions {
  method?: string;
  /** Raw body (e.g. FormData). Mutually exclusive with `json`. */
  body?: BodyInit | null;
  /** JSON-serializable body; sets Content-Type automatically. */
  json?: unknown;
  /** Retry once after a successful token refresh on 401. Default true. */
  allowRefreshRetry?: boolean;
}

async function request(path: string, options: RequestOptions = {}): Promise<Response> {
  const doFetch = () => {
    const headers: Record<string, string> = {};
    if (options.json !== undefined) headers['Content-Type'] = 'application/json';
    // Bearer only when a session exists — anonymous requests stay anonymous.
    const token = getAccessToken();
    if (token) headers.Authorization = `Bearer ${token}`;
    return fetch(`${BASE_URL}${path}`, {
      method: options.method ?? 'GET',
      headers,
      body: options.json !== undefined ? JSON.stringify(options.json) : options.body,
    });
  };

  let res = await doFetch();

  if (res.status === 401 && options.allowRefreshRetry !== false && getRefreshToken()) {
    const refreshed = await refreshTokens();
    if (refreshed) {
      res = await doFetch(); // exactly one retry
    }
  }

  return res;
}

export const api = {
  /* ── Auth ─────────────────────────────────────────────── */

  async login(dto: LoginRequest): Promise<AuthResponse> {
    const res = await request('/auth/login', {
      method: 'POST',
      json: dto,
      allowRefreshRetry: false,
    });
    return handleResponse<AuthResponse>(res);
  },

  async register(dto: RegisterRequest): Promise<AuthResponse> {
    const res = await request('/auth/register', {
      method: 'POST',
      json: dto,
      allowRefreshRetry: false,
    });
    return handleResponse<AuthResponse>(res);
  },

  /**
   * Best-effort refresh-token revocation. `accessToken` is passed explicitly
   * because the local session is usually cleared before this call.
   */
  async logout(accessToken: string | null, refreshToken: string): Promise<void> {
    try {
      await fetch(`${BASE_URL}/auth/logout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        },
        body: JSON.stringify({ refreshToken }),
      });
    } catch {
      // Network failure is fine — the local session is already dropped.
    }
  },

  /* ── Profile self-service (CB-1) ───────────────────────── */

  /** GET /api/v1/me — current user's profile. */
  async getProfile(): Promise<Profile> {
    const res = await request('/me');
    if (!res.ok) throw new Error('Не удалось загрузить профиль');
    return handleResponse<Profile>(res);
  },

  /** PUT /api/v1/me — partial update; undefined fields keep current values. */
  async updateProfile(dto: UpdateProfileRequest): Promise<Profile> {
    const res = await request('/me', { method: 'PUT', json: dto });
    if (!res.ok) {
      const body = (await res.json().catch(() => null)) as ApiResult<Profile> | null;
      throw new Error(body?.error ?? 'Не удалось сохранить профиль');
    }
    return handleResponse<Profile>(res);
  },

  /** POST /api/v1/me/password — verify current, set new. */
  async changePassword(dto: ChangePasswordRequest): Promise<void> {
    const res = await request('/me/password', { method: 'POST', json: dto });
    if (!res.ok) {
      const body = (await res.json().catch(() => null)) as ApiResult<unknown> | null;
      throw new Error(body?.error ?? 'Не удалось изменить пароль');
    }
  },

  /* ── Notification center (CB-2) ───────────────────── */

  /** GET /api/v1/notifications — newest-first page + unread count (any role). */
  async getNotifications(page = 1, pageSize = 20): Promise<NotificationList> {
    const res = await request(`/notifications?page=${page}&pageSize=${pageSize}`);
    if (!res.ok) throw new Error('Не удалось загрузить уведомления');
    return handleResponse<NotificationList>(res);
  },

  /** POST /api/v1/notifications/read-all — mark every unread row read. */
  async markNotificationsRead(): Promise<number> {
    const res = await request('/notifications/read-all', { method: 'POST' });
    if (!res.ok) throw new Error('Не удалось отметить уведомления');
    return handleResponse<number>(res);
  },

  /* ── Address book (CB-3) + DaData suggest (task-218) ──── */

  async getAddressBook(): Promise<AddressBookEntry[]> {
    const res = await request('/address-book');
    if (!res.ok) throw new Error('Не удалось загрузить адреса');
    return handleResponse<{ entries: AddressBookEntry[] }>(res).then((b) => b.entries);
  },

  async createAddressBookEntry(dto: CreateAddressRequest): Promise<AddressBookEntry> {
    const res = await request('/address-book', { method: 'POST', json: dto });
    if (!res.ok) throw new Error('Не удалось сохранить адрес');
    return handleResponse<AddressBookEntry>(res);
  },

  async updateAddressBookEntry(id: string, dto: UpdateAddressRequest): Promise<AddressBookEntry> {
    const res = await request(`/address-book/${id}`, { method: 'PUT', json: dto });
    if (!res.ok) throw new Error('Не удалось изменить адрес');
    return handleResponse<AddressBookEntry>(res);
  },

  async deleteAddressBookEntry(id: string): Promise<void> {
    const res = await request(`/address-book/${id}`, { method: 'DELETE' });
    if (!res.ok) throw new Error('Не удалось удалить адрес');
    await handleResponse<null>(res);
  },

  async setDefaultAddressBookEntry(id: string): Promise<void> {
    const res = await request(`/address-book/${id}/set-default`, { method: 'POST' });
    if (!res.ok) throw new Error('Не удалось выбрать адрес по умолчанию');
    await handleResponse<null>(res);
  },

  async suggestAddresses(query: string, count = 8): Promise<AddressSuggestion[]> {
    const res = await request(`/addresses/suggest?query=${encodeURIComponent(query)}&count=${count}`);
    if (!res.ok) return []; // suggestions are decorative — degrade to plain input
    return handleResponse<AddressSuggestion[]>(res);
  },

  /* ── Catalog / quoting ────────────────────────────────── */

  async getMaterials(): Promise<Material[]> {
    const res = await request('/materials');
    if (!res.ok) throw new Error('Не удалось загрузить материалы');
    return handleResponse<Material[]>(res);
  },

  /* ── MS-1: provider material stock by color ───────────── */

  async getProviderMaterialStock(): Promise<MaterialStockRow[]> {
    const res = await request('/providers/material-stock');
    return handleResponse<MaterialStockRow[]>(res);
  },

  async saveProviderMaterialStock(items: MaterialStockItem[]): Promise<MaterialStockRow[]> {
    const res = await request('/providers/material-stock', {
      method: 'PUT',
      json: items,
    });
    return handleResponse<MaterialStockRow[]>(res);
  },

  /* ── CB-4: provider earnings dashboard (read-only) ─── */

  async getProviderEarnings(): Promise<ProviderEarnings> {
    const res = await request('/providers/earnings');
    return handleResponse<ProviderEarnings>(res);
  },

  async uploadModel(file: File): Promise<ModelFileResponse> {
    const formData = new FormData();
    formData.append('file', file);

    const res = await request('/models/upload', {
      method: 'POST',
      body: formData,
    });

    return handleResponse<ModelFileResponse>(res);
  },

  async getModel(fileId: string): Promise<ModelFileResponse> {
    const res = await request(`/models/${fileId}`);
    return handleResponse<ModelFileResponse>(res);
  },

  /**
   * cc4909b4: raw mesh bytes for the interactive 3D viewer (party-scoped
   * endpoint — 404 for strangers). ArrayBuffer so three.js loaders parse it.
   */
  async fetchModelContent(fileId: string): Promise<ArrayBuffer> {
    const res = await request(`/models/${fileId}/content`);
    if (!res.ok) throw new Error(`model-content-${res.status}`);
    return res.arrayBuffer();
  },

  async createQuote(requestData: QuoteRequest): Promise<QuoteResponse> {
    const res = await request('/quotes', {
      method: 'POST',
      json: requestData,
    });

    return handleResponse<QuoteResponse>(res);
  },

  /**
   * MW-3 (40064910): MakerWorld link → quote-form prefill (MW-2 endpoint,
   * 1ca5f3cf). Public + rate-limited; found:false arrives as a 200 —
   * only transport/429 rejects here and the caller falls back to manual
   * input (spec §5.4), so callers use try/catch, not result inspection.
   */
  async prefillFromLink(url: string): Promise<MakerWorldPrefillResultDto> {
    const res = await request('/quotes/prefill-from-link', {
      method: 'POST',
      json: { url },
    });

    return handleResponse<MakerWorldPrefillResultDto>(res);
  },

  /* ── TASK-197: performer matching + ordering ─────────── */

  async getQuotePerformers(quoteId: string): Promise<PerformerMatches> {
    const res = await request(`/quotes/${quoteId}/performers`);
    return handleResponse<PerformerMatches>(res);
  },

  // G8-D2: public self-pickup points of a provider (address-only, active only).
  async getProviderPickupPoints(providerId: string): Promise<PublicPickupPoint[]> {
    const res = await request(`/providers/${providerId}/pickup-points`);
    return handleResponse<PublicPickupPoint[]>(res);
  },

  // beb17227: public carrier pickup-point catalog (paged, active-only;
  // 63b51a7e: carrier null = all carriers for the checkout map window;
  // b0bef46e: optional server-side settlement filter — the map fetches a
  // per-city window instead of the old first-3000-rows client cap).
  async getDeliveryPoints(
    carrier: 'ozon' | 'cdek' | 'russianpost' | null,
    offset = 0,
    limit = 50,
    city?: string | null,
  ): Promise<DeliveryPointPage> {
    const qs = new URLSearchParams({ offset: String(offset), limit: String(limit) });
    if (carrier) qs.set('carrier', carrier);
    const trimmedCity = city?.trim();
    if (trimmedCity) qs.set('city', trimmedCity);
    const res = await request(`/delivery-points?${qs.toString()}`);
    return handleResponse<DeliveryPointPage>(res);
  },

  // 63b51a7e: clustered catalog cities for the checkout-map city selector.
  async getDeliveryPointCities(): Promise<DeliveryPointCities> {
    const res = await request('/delivery-points/cities');
    return handleResponse<DeliveryPointCities>(res);
  },

  // 63b51a7e: best-effort IP→city for the map's initial center (server-side,
  // keyless; city=null keeps the default).
  async getIpCity(): Promise<IpCityResult> {
    const res = await request('/geo/ip-city');
    return handleResponse<IpCityResult>(res);
  },

  // beb17227: non-mutating Ozon Доставка tariff расчёт to one pickup point
  // (POST /v1/order/checkout behind the server quote-only gate). Phone comes
  // from the caller's profile server-side unless overridden here.
  async ozonQuote(input: OzonQuoteInput): Promise<OzonQuoteResult> {
    const res = await request('/delivery-rates/ozon/quote', {
      method: 'POST',
      json: {
        deliveryPointExternalId: input.deliveryPointExternalId,
        weightGrams: input.weightGrams,
        lengthCm: input.lengthCm,
        widthCm: input.widthCm,
        heightCm: input.heightCm,
        declaredValueRub: input.declaredValueRub ?? null,
        recipientPhone: input.recipientPhone ?? null,
      },
    });
    return handleResponse<OzonQuoteResult>(res);
  },

  // beb17227: parcel-fit availability of one pickup point (restrictions check).
  async ozonCheckAvailability(input: OzonQuoteInput): Promise<OzonPointAvailability> {
    const res = await request('/delivery-rates/ozon/check-availability', {
      method: 'POST',
      json: {
        deliveryPointExternalId: input.deliveryPointExternalId,
        weightGrams: input.weightGrams,
        lengthCm: input.lengthCm,
        widthCm: input.widthCm,
        heightCm: input.heightCm,
        declaredValueRub: input.declaredValueRub ?? null,
        recipientPhone: input.recipientPhone ?? null,
      },
    });
    return handleResponse<OzonPointAvailability>(res);
  },

  async createOrder(
    quoteId: string,
    shippingAddress: string | null,
    notes: string | null,
    deliveryMethod?: number,
    pickupPointId?: string,
    printColor?: string | null,
    // 527b0a7f: recipient phone snapshot (typed ?? profile fallback is
    // resolved server-side); null keeps the legacy payload identical.
    recipientPhone?: string | null,
  ): Promise<OrderSummary> {
    const res = await request('/orders', {
      method: 'POST',
      json: {
        quoteId,
        shippingAddress,
        notes,
        deliveryMethod: deliveryMethod ?? null,
        pickupPointId: pickupPointId ?? null,
        printColor: printColor ?? null,
        recipientPhone: recipientPhone ?? null,
      },
    });
    return handleResponse<OrderSummary>(res);
  },

  // ── Shop: catalog / cart ───────────────────────────

  // ── TASK-247: public provider catalog ────────────

  async getProviderCatalog(params: {
    q?: string; city?: string; technology?: string; verified?: boolean; offset?: number; limit?: number;
  } = {}): Promise<ProviderCatalogBrowseResult> {
    const qs = new URLSearchParams();
    const q = params.q?.trim();
    if (q) qs.set('q', q);
    if (params.city?.trim()) qs.set('city', params.city.trim());
    if (params.technology?.trim()) qs.set('technology', params.technology.trim());
    if (params.verified) qs.set('verified', 'true');
    if (params.offset && params.offset > 0) qs.set('offset', String(params.offset));
    if (params.limit) qs.set('limit', String(params.limit));
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    const res = await request(`/catalog/providers${suffix}`);
    return handleResponse<ProviderCatalogBrowseResult>(res);
  },

  async getProviderCatalogDetail(providerUserId: string): Promise<ProviderCatalogDetail> {
    const res = await request(`/catalog/providers/${providerUserId}`);
    return handleResponse<ProviderCatalogDetail>(res);
  },

  async getProducts(params: { materialId?: string; providerId?: string; q?: string } = {}): Promise<ProductSummary[]> {
    const qs = new URLSearchParams();
    if (params.materialId) qs.set('materialId', params.materialId);
    if (params.providerId) qs.set('providerId', params.providerId);
    if (params.q) qs.set('q', params.q);
    const suffix = qs.toString() ? `?${qs}` : '';
    const res = await request(`/products${suffix}`);
    return handleResponse<ProductSummary[]>(res);
  },

  /** 3d4c488f: paged shop browse — offset/limit + filtered total for the pager. */
  async getProductPage(params: ProductPageQuery = {}): Promise<ProductPage> {
    const qs = new URLSearchParams();
    if (params.materialId) qs.set('materialId', params.materialId);
    if (params.providerId) qs.set('providerId', params.providerId);
    if (params.categoryId) qs.set('categoryId', params.categoryId);
    if (params.q) qs.set('q', params.q);
    if (params.minPriceRub != null) qs.set('minPriceRub', String(params.minPriceRub));
    if (params.maxPriceRub != null) qs.set('maxPriceRub', String(params.maxPriceRub));
    qs.set('offset', String(params.offset ?? 0));
    qs.set('limit', String(params.limit ?? 24));
    const res = await request(`/products/page?${qs.toString()}`);
    return handleResponse<ProductPage>(res);
  },

  /** 3d4c488f: active category tree for the shop chips / product forms. */
  async getCategories(): Promise<ProductCategoryNode[]> {
    const res = await request('/categories');
    return handleResponse<ProductCategoryNode[]>(res);
  },

  /** 3d4c488f: admin cabinet — full tree incl. inactive nodes. */
  async getAdminCategories(): Promise<ProductCategoryNode[]> {
    const res = await request('/categories/admin');
    return handleResponse<ProductCategoryNode[]>(res);
  },

  async createCategory(dto: ProductCategoryInput): Promise<ProductCategoryNode> {
    const res = await request('/categories', { method: 'POST', json: dto });
    return handleResponse<ProductCategoryNode>(res);
  },

  async updateCategory(id: string, dto: ProductCategoryInput): Promise<ProductCategoryNode> {
    const res = await request(`/categories/${id}`, { method: 'PUT', json: dto });
    return handleResponse<ProductCategoryNode>(res);
  },

  async deleteCategory(id: string): Promise<void> {
    const res = await request(`/categories/${id}`, { method: 'DELETE' });
    await handleResponse<null>(res);
  },

  async getProduct(id: string): Promise<ProductDetail> {
    const res = await request(`/products/${id}`);
    return handleResponse<ProductDetail>(res);
  },

  async getMyProducts(): Promise<ProductDetail[]> {
    const res = await request('/products/mine');
    return handleResponse<ProductDetail[]>(res);
  },

  /** 423ee061: paged seller listings — status filter + title search. */
  async getMyProductsPage(status: string, q: string, offset: number, limit: number): Promise<ProductMinePage> {
    const qs = new URLSearchParams({ offset: String(offset), limit: String(limit) });
    if (status) qs.set('status', status);
    if (q) qs.set('q', q);
    const res = await request(`/products/mine/page?${qs}`);
    return handleResponse<ProductMinePage>(res);
  },

  async createProduct(dto: ProductDraft): Promise<ProductDetail> {
    const res = await request('/products', { method: 'POST', json: dto });
    return handleResponse<ProductDetail>(res);
  },

  async updateProduct(id: string, dto: ProductDraft): Promise<ProductDetail> {
    const res = await request(`/products/${id}`, { method: 'PUT', json: dto });
    return handleResponse<ProductDetail>(res);
  },

  async archiveProduct(id: string): Promise<void> {
    const res = await request(`/products/${id}`, { method: 'DELETE' });
    if (!res.ok) throw new Error('Не удалось удалить товар');
  },

  async getCart(): Promise<Cart> {
    const res = await request('/cart');
    return handleResponse<Cart>(res);
  },

  async addToCart(
    productId: string,
    quantity: number,
    // a1a3b8f4: product-option selections — only chosen rows ride the body.
    selectedOptions?: { optionId: string; valueId?: string; quantity?: number }[],
  ): Promise<Cart> {
    const res = await request('/cart/items', {
      method: 'POST',
      json: { productId, quantity, ...(selectedOptions?.length ? { selectedOptions } : {}) },
    });
    return handleResponse<Cart>(res);
  },

  // 3830fe0d: TASK-201 quote-print line — the calculated job joins the cart
  // (server re-prices via performer matching); ordering happens at cart
  // checkout, never straight from the quote page.
  async addQuotePrint(quoteId: string, providerId: string, deliveryMethod: number): Promise<Cart> {
    const res = await request('/cart/quote-print', {
      method: 'POST',
      json: { quoteId, providerId, deliveryMethod },
    });
    return handleResponse<Cart>(res);
  },

  async updateCartItem(itemId: string, quantity: number): Promise<Cart> {
    const res = await request(`/cart/items/${itemId}`, { method: 'PUT', json: { quantity } });
    return handleResponse<Cart>(res);
  },

  async removeCartItem(itemId: string): Promise<Cart> {
    const res = await request(`/cart/items/${itemId}`, { method: 'DELETE' });
    return handleResponse<Cart>(res);
  },

  // 2026-09-03 (Артем): tariff preview for the picked ПВЗ — parcel
  // mass/dims/value from the server's quote calculation (+packaging
  // reserve); powers the point card on the checkout map.
  async cartDeliveryQuote(
    carrier: string,
    externalId: string,
  ): Promise<import('../types/delivery').CartDeliveryQuote> {
    const res = await request('/cart/delivery-quote', {
      method: 'POST',
      json: { carrier, externalId },
    });
    return handleResponse<import('../types/delivery').CartDeliveryQuote>(res);
  },

  async checkoutCart(
    shippingAddress: string | null,
    notes: string | null,
    deliveryMethod?: number,
    pickupPointId?: string,
    printColor?: string | null,
    // ecfe2257: 0 any / 1 multicolor — sent only when no concrete color; the
    // backend normalizes (color set → concrete). Legacy calls omit it = any.
    printColorMode?: number,
    // 63b51a7e: carrier ПВЗ picked on the checkout map — validated + snapshotted
    // onto every order server-side; null keeps the legacy payload byte-identical.
    carrierPoint?: CarrierPointSelection | null,
    // 527b0a7f: recipient phone typed at checkout — snapshotted onto every
    // order; the server falls back to the profile number on null and saves a
    // first-typed one back to the profile.
    recipientPhone?: string | null,
  ): Promise<OrderSummary[]> {
    const res = await request('/cart/checkout', {
      method: 'POST',
      json: {
        shippingAddress,
        notes,
        deliveryMethod: deliveryMethod ?? null,
        pickupPointId: pickupPointId ?? null,
        // MS-2 (TASK-004f86db): color parity with the quote flow — null when
        // the cart has no print jobs or the customer kept the default pick.
        printColor: printColor ?? null,
        printColorMode: printColor ?? null ? 2 : (printColorMode ?? 0),
        // 63b51a7e: map pick rides the checkout only when chosen.
        carrierPointCarrier: carrierPoint?.carrier ?? null,
        carrierPointExternalId: carrierPoint?.externalId ?? null,
        // 527b0a7f: phone rides the checkout only when provided/valid.
        recipientPhone: recipientPhone ?? null,
      },
    });
    return handleResponse<OrderSummary[]>(res);
  },

  async getMyOrders(): Promise<OrderSummary[]> {
    const res = await request('/orders');
    return handleResponse<OrderSummary[]>(res);
  },

  // 4171ba09: server-side paginated customer orders (slim cabinet lists).
  async getMyOrdersPage(filter: string, offset: number, limit: number): Promise<OrderSummaryPage> {
    const qs = new URLSearchParams({ offset: String(offset), limit: String(limit) });
    if (filter && filter !== 'all') qs.set('filter', filter);
    const res = await request(`/orders/page?${qs}`);
    return handleResponse<OrderSummaryPage>(res);
  },

  // ── TASK-235: order lifecycle detail + confirm/dispute ─

  async getOrder(orderId: string): Promise<OrderDetail> {
    const res = await request(`/orders/${orderId}`);
    return handleResponse<OrderDetail>(res);
  },

  async confirmOrderResult(orderId: string, comment?: string): Promise<OrderDetail> {
    const res = await request(`/orders/${orderId}/result/confirm`, {
      method: 'POST',
      json: { comment: comment ?? null },
    });
    return handleResponse<OrderDetail>(res);
  },

  async disputeOrderResult(orderId: string, reason: string, photoUrls: string[] = []): Promise<OrderDetail> {
    const res = await request(`/orders/${orderId}/result/dispute`, {
      method: 'POST',
      json: { reason, photoUrls },
    });
    return handleResponse<OrderDetail>(res);
  },

  // dc25865a (audit-12 §5 Card A): stage one customer dispute-evidence photo;
  // the returned URL rides the next disputeOrderResult call.
  async uploadDisputeEvidencePhoto(orderId: string, file: File): Promise<MediaUploadResponse> {
    const formData = new FormData();
    formData.append('file', file);
    const res = await request(`/orders/${orderId}/result/dispute/attachments`, {
      method: 'POST',
      body: formData,
    });
    return handleResponse<MediaUploadResponse>(res);
  },

  // SD-3 (d36195ae): start (or retry) the Safe Deal payment for an unpaid
  // Pending order — returns the YooKassa confirmation URL the customer is
  // redirected to. 400 while the rail is disabled (UI hides the CTA then).
  async payOrder(orderId: string): Promise<SafeDealPaymentInitiation> {
    const res = await request(`/orders/${orderId}/pay`, { method: 'POST' });
    return handleResponse<SafeDealPaymentInitiation>(res);
  },

  // TASK-6bdd9dc3 (audit P1): customer cancels an order before work starts
  // (Pending/Accepted only — server enforces the same rule). Optional
  // reason rides CancelOrderDto and lands in the cancellation email.
  async cancelOrder(orderId: string, reason?: string): Promise<OrderDetail> {
    const res = await request(`/orders/${orderId}/cancel`, {
      method: 'POST',
      json: { reason: reason?.trim() || null },
    });
    return handleResponse<OrderDetail>(res);
  },

  // audit5-B1a (500f41b1): customer confirms delivery of a Shipped order —
  // server completes it through the same guarded transition the provider
  // path uses (completion email included), closing the lazy-provider
  // completion deadlock (reviews gate).
  async confirmDelivery(orderId: string): Promise<OrderDetail> {
    const res = await request(`/orders/${orderId}/confirm-delivery`, { method: 'POST' });
    return handleResponse<OrderDetail>(res);
  },

  // CB-5 (1715d4b7): repeat a Completed order — the server re-fills the cart
  // with every line that can honestly be re-added (live product validation,
  // quote recalculated on the same file + current-match re-verification) and
  // reports the rest per line («файл удалён» per deleted model file).
  async repeatOrder(orderId: string): Promise<RepeatOrderResult> {
    const res = await request(`/orders/${orderId}/repeat`, { method: 'POST' });
    return handleResponse<RepeatOrderResult>(res);
  },

  // ── CB-6 (a90f4a83): customer favorites (products + performers) ──

  /** GET /api/v1/favorites — owner's list, newest first (any role). */
  async getFavorites(): Promise<FavoriteList> {
    const res = await request('/favorites');
    if (!res.ok) throw new Error('Не удалось загрузить избранное');
    return handleResponse<FavoriteList>(res);
  },

  /** GET /api/v1/favorites/ids — compact id sets for heart states. */
  async getFavoriteIds(): Promise<FavoriteIds> {
    const res = await request('/favorites/ids');
    return handleResponse<FavoriteIds>(res);
  },

  /** POST toggle a product favorite; returns the new state. */
  async toggleProductFavorite(productId: string): Promise<FavoriteToggleResult> {
    const res = await request(`/favorites/products/${productId}/toggle`, { method: 'POST' });
    return handleResponse<FavoriteToggleResult>(res);
  },

  /** POST toggle a performer favorite; returns the new state. */
  async toggleProviderFavorite(providerUserId: string): Promise<FavoriteToggleResult> {
    const res = await request(`/favorites/providers/${providerUserId}/toggle`, { method: 'POST' });
    return handleResponse<FavoriteToggleResult>(res);
  },

  // ── TASK-246: counter-offer negotiation on Pending custom-quote orders ─

  async createCounterOffer(
    orderId: string,
    proposedPriceRub: number,
    proposedDeliveryUtc: string | null,
    message: string,
  ): Promise<CounterOffer> {
    const res = await request(`/orders/${orderId}/counter-offers`, {
      method: 'POST',
      json: { proposedPriceRub, proposedDeliveryUtc, message },
    });
    return handleResponse<CounterOffer>(res);
  },

  async acceptCounterOffer(orderId: string, offerId: string): Promise<CounterOffer> {
    const res = await request(`/orders/${orderId}/counter-offers/${offerId}/accept`, { method: 'POST' });
    return handleResponse<CounterOffer>(res);
  },

  async rejectCounterOffer(orderId: string, offerId: string): Promise<CounterOffer> {
    const res = await request(`/orders/${orderId}/counter-offers/${offerId}/reject`, { method: 'POST' });
    return handleResponse<CounterOffer>(res);
  },

  // ── 33f30c97: order-scoped customer↔performer chat ─

  async getOrderChat(orderId: string, beforeUtc?: string): Promise<OrderChatThread> {
    const query = beforeUtc ? `?beforeUtc=${encodeURIComponent(beforeUtc)}` : '';
    const res = await request(`/orders/${orderId}/chat${query}`);
    return handleResponse<OrderChatThread>(res);
  },

  async sendOrderChatMessage(
    orderId: string,
    body: string,
    photoUrls: string[] = [],
  ): Promise<OrderChatMessage> {
    const res = await request(`/orders/${orderId}/chat`, {
      method: 'POST',
      json: { body, photoUrls },
    });
    return handleResponse<OrderChatMessage>(res);
  },

  // fa58b0eb: upload one chat photo attachment (JPEG/PNG/WebP, ≤10MB) —
  // party-scoped + posting-gated server-side; returns the hosted URL that
  // rides the next sendOrderChatMessage call.
  async uploadOrderChatPhoto(orderId: string, file: File): Promise<MediaUploadResponse> {
    const formData = new FormData();
    formData.append('file', file);
    const res = await request(`/orders/${orderId}/chat/attachments`, {
      method: 'POST',
      body: formData,
    });
    return handleResponse<MediaUploadResponse>(res);
  },

  async markOrderChatRead(orderId: string): Promise<boolean> {
    const res = await request(`/orders/${orderId}/chat/read`, { method: 'POST' });
    return handleResponse<boolean>(res);
  },

  // ── Provider Portal ──────────────────────────────────

  async registerProvider(dto: RegisterProvider): Promise<ProviderProfileDetail> {
    const res = await request('/providers/register', {
      method: 'POST',
      json: dto,
    });
    return handleResponse<ProviderProfileDetail>(res);
  },

  async getProviderProfile(): Promise<ProviderProfileDetail> {
    const res = await request('/providers/profile');
    return handleResponse<ProviderProfileDetail>(res);
  },

  async updateProviderProfile(dto: UpdateProviderProfile): Promise<ProviderProfileDetail> {
    const res = await request('/providers/profile', {
      method: 'PUT',
      json: dto,
    });
    return handleResponse<ProviderProfileDetail>(res);
  },

  async toggleProviderAvailability(isAvailable: boolean): Promise<void> {
    await request('/providers/availability', {
      method: 'POST',
      json: { isAvailable },
    });
  },

  // SD-4: Safe Deal payout destination — masked read + replace.
  async getProviderPayoutMethod(): Promise<ProviderPayoutMethod> {
    const res = await request('/providers/cabinet/payout-method');
    return handleResponse<ProviderPayoutMethod>(res);
  },

  async updateProviderPayoutMethod(dto: UpdateProviderPayoutMethod): Promise<ProviderPayoutMethod> {
    const res = await request('/providers/cabinet/payout-method', {
      method: 'PUT',
      json: dto,
    });
    return handleResponse<ProviderPayoutMethod>(res);
  },

  // 21f0c169: PayoutsData widget bootstrap (gateway agentId for account_id).
  async getProviderPayoutWidgetConfig(): Promise<ProviderPayoutWidgetConfig> {
    const res = await request('/providers/cabinet/payout-widget-config');
    return handleResponse<ProviderPayoutWidgetConfig>(res);
  },

  // 138b4149: cabinet delivery settings — module flags + the sender postal
  // index (index_from) for Почта России live tariffs.
  async getDeliverySettings(): Promise<DeliverySettings> {
    const res = await request('/providers/cabinet/delivery');
    return handleResponse<DeliverySettings>(res);
  },

  async updateDeliverySettings(dto: UpdateDeliverySettings): Promise<DeliverySettings> {
    const res = await request('/providers/cabinet/delivery', {
      method: 'PUT',
      json: dto,
    });
    return handleResponse<DeliverySettings>(res);
  },

  async addPrinter(dto: CreatePrinter): Promise<PrinterDetail> {
    const res = await request('/providers/printers', {
      method: 'POST',
      // 0258c2c7: API binds enums as ordinals only (AddControllers default
      // JSON) — map canonical string unions to numbers at the boundary,
      // mirroring how responses are normalized on ingest.
      json: printerWireDto(dto),
    });
    return handleResponse<PrinterDetail>(res);
  },

  async updatePrinter(printerId: string, dto: UpdatePrinter): Promise<PrinterDetail> {
    const res = await request(`/providers/printers/${printerId}`, {
      method: 'PUT',
      json: printerWireDto(dto),
    });
    return handleResponse<PrinterDetail>(res);
  },

  async deletePrinter(printerId: string): Promise<void> {
    await request(`/providers/printers/${printerId}`, { method: 'DELETE' });
  },

  /* 16215cbc: print-file preparation (Bambu Studio CLI slicing jobs). */

  async getSlicingContext(orderId: string): Promise<SlicingContext> {
    const res = await request(`/slicing/orders/${orderId}/context`);
    return handleResponse<SlicingContext>(res);
  },

  async createSlicingJob(
    orderId: string,
    printerId: string,
    modelFileId: string,
    plateIndex = 0,
    exportKind: 'gcode' | '3mf' = 'gcode',
  ): Promise<SlicingJob> {
    const res = await request(`/slicing/orders/${orderId}/jobs`, {
      method: 'POST',
      json: { printerId, modelFileId, plateIndex, exportKind },
    });
    return handleResponse<SlicingJob>(res);
  },

  async listSlicingJobs(orderId: string): Promise<SlicingJob[]> {
    const res = await request(`/slicing/orders/${orderId}/jobs`);
    return handleResponse<SlicingJob[]>(res);
  },

  /** Streams the completed artifact (.gcode.3mf or project .3mf, auth header included). */
  async downloadSlicingArtifact(jobId: string): Promise<Blob> {
    const res = await request(`/slicing/jobs/${jobId}/artifact`);
    if (!res.ok) throw new Error('Файл недоступен');
    return res.blob();
  },

  /** 1a6b6f55: raw order model download — served to the order's provider by
   * the cc4909b4 viewer-content rules (directed quote or order relationship). */
  async downloadModelFile(modelFileId: string): Promise<Blob> {
    const res = await request(`/models/${modelFileId}/content`);
    if (!res.ok) throw new Error('Файл недоступен');
    return res.blob();
  },

  async getPrinterSlicingProfiles(printerId: string): Promise<PrinterSlicingProfiles> {
    const res = await request(`/providers/printers/${printerId}/slicing-profiles`);
    return handleResponse<PrinterSlicingProfiles>(res);
  },

  async updatePrinterSlicingProfiles(
    printerId: string,
    dto: { machineProfileJson?: string | null; processProfileJson?: string | null; filamentProfileJson?: string | null },
  ): Promise<PrinterSlicingProfiles> {
    const res = await request(`/providers/printers/${printerId}/slicing-profiles`, {
      method: 'PUT',
      json: dto,
    });
    return handleResponse<PrinterSlicingProfiles>(res);
  },

  /* 15c2a311 (12754a4b part 2): «Мой налог» cabinet — SMS connect wizard,
     connected profile card and per-order receipts. All endpoints are
     token-free by backend contract. */

  async startNalogConnect(phone: string): Promise<NalogChallengeStarted> {
    const res = await request('/providers/nalog/connect/start', {
      method: 'POST',
      json: { phone },
    });
    return handleResponse<NalogChallengeStarted>(res);
  },

  async verifyNalogConnect(code: string): Promise<NalogConnection> {
    const res = await request('/providers/nalog/connect/verify', {
      method: 'POST',
      json: { code },
    });
    return handleResponse<NalogConnection>(res);
  },

  /** Connected profile card; null when the provider never connected. */
  async getNalogConnection(): Promise<NalogConnection | null> {
    const res = await request('/providers/nalog/connection');
    return handleResponse<NalogConnection | null>(res);
  },

  async disconnectNalog(): Promise<void> {
    const res = await request('/providers/nalog/disconnect', { method: 'POST' });
    await handleResponse<unknown>(res);
  },

  async getNalogReceipt(orderId: string): Promise<NalogReceipt> {
    const res = await request(`/providers/nalog/receipts/${orderId}`);
    return handleResponse<NalogReceipt>(res);
  },

  /** Default comment «Чек сформирован ошибочно» comes from the backend. */
  async cancelNalogReceipt(orderId: string): Promise<NalogReceipt> {
    const res = await request(`/providers/nalog/receipts/${orderId}/cancel`, {
      method: 'POST',
      json: { comment: null },
    });
    return handleResponse<NalogReceipt>(res);
  },

  /** Fetch-from-tax + cache (POST) then stream the stored file (GET). */
  async downloadNalogReceiptFile(orderId: string): Promise<Blob> {
    const priming = await request(`/providers/nalog/receipts/${orderId}/file`, { method: 'POST' });
    if (!priming.ok) {
      await handleResponse<NalogReceipt>(priming); // surfaces the readable Result error
    }
    const res = await request(`/providers/nalog/receipts/${orderId}/file`);
    if (!res.ok) throw new Error('Файл чека недоступен');
    return res.blob();
  },

  /** PF-3 «Из каталога» picker: public reference data (PF-1 GET /api/v1/printer-catalog, no auth). */
  async getPrinterCatalog(): Promise<PrinterModelCatalogResponse> {
    const res = await request('/printer-catalog');
    return handleResponse<PrinterModelCatalogResponse>(res);
  },

  async addPrinterMaterial(printerId: string, dto: AddProviderMaterial): Promise<ProviderMaterial> {
    const res = await request(`/providers/printers/${printerId}/materials`, {
      method: 'POST',
      json: dto,
    });
    return handleResponse<ProviderMaterial>(res);
  },

  async removePrinterMaterial(providerMaterialId: string): Promise<void> {
    await request(`/providers/materials/${providerMaterialId}`, { method: 'DELETE' });
  },

  async getProviderOrders(): Promise<ProviderOrder[]> {
    const res = await request('/providers/orders');
    return handleResponse<ProviderOrder[]>(res);
  },

  // 4171ba09: server-side paginated provider queue (slim cabinet lists).
  async getProviderOrdersPage(filter: string, offset: number, limit: number): Promise<ProviderOrderPage> {
    const qs = new URLSearchParams({ offset: String(offset), limit: String(limit) });
    if (filter && filter !== 'all') qs.set('filter', filter);
    const res = await request(`/providers/orders/page?${qs}`);
    return handleResponse<ProviderOrderPage>(res);
  },

  async acceptProviderOrder(orderId: string, notes?: string): Promise<ProviderOrder> {
    const res = await request(`/providers/orders/${orderId}/accept`, {
      method: 'POST',
      json: { notes: notes ?? null },
    });
    return handleResponse<ProviderOrder>(res);
  },

  async rejectProviderOrder(orderId: string): Promise<void> {
    await request(`/providers/orders/${orderId}/reject`, { method: 'POST' });
  },

  async updateProviderOrderStatus(
    orderId: string,
    status: string | WebOrderStatus,
    trackingNumber?: string,
    estimatedDeliveryUtc?: string,
  ): Promise<ProviderOrder> {
    const res = await request(`/providers/orders/${orderId}/status`, {
      method: 'PUT',
      json: {
        // TASK-254: the API binds UpdateOrderStatusDto with numeric enum
        // values — the string union 400s at the model binder.
        status: orderStatusToJson(status as WebOrderStatus),
        trackingNumber: trackingNumber ?? null,
        estimatedDeliveryUtc: estimatedDeliveryUtc ?? null,
      },
    });
    return handleResponse<ProviderOrder>(res);
  },

  // audit4-A3 (b10d4e95): provider adds the post-ship tracking number the
  // no-tracking Shipped email promised. One-shot — the API accepts it only
  // while the order is Shipped and tracking is still empty, then re-sends
  // the customer OrderShipped email with the fresh number.
  async patchProviderOrderTracking(
    orderId: string,
    trackingNumber: string,
    estimatedDeliveryUtc?: string,
  ): Promise<ProviderOrder> {
    const res = await request(`/providers/orders/${orderId}/tracking`, {
      method: 'PATCH',
      json: {
        trackingNumber,
        estimatedDeliveryUtc: estimatedDeliveryUtc ?? null,
      },
    });
    return handleResponse<ProviderOrder>(res);
  },

  // TASK-235: provider publishes the print result — photo URLs + optional
  // note. Attaching from Printing moves the order to Printed; after that the
  // customer confirms or disputes.
  async attachProviderOrderResult(
    orderId: string,
    photos: Array<{ url: string; caption?: string | null }>,
    note?: string | null,
  ): Promise<ProviderOrder> {
    const res = await request(`/providers/orders/${orderId}/result`, {
      method: 'POST',
      json: {
        photos: photos.map((p) => ({ url: p.url, caption: p.caption ?? null })),
        note: note ?? null,
      },
    });
    return handleResponse<ProviderOrder>(res);
  },

  // TASK-250: provider shares in-progress photos while the order is
  // Accepted or Printing. Informational only — no status change.
  async attachProviderOrderProgress(
    orderId: string,
    photos: Array<{ url: string; caption?: string | null }>,
  ): Promise<ProviderOrder> {
    const res = await request(`/providers/orders/${orderId}/progress`, {
      method: 'POST',
      json: {
        photos: photos.map((p) => ({ url: p.url, caption: p.caption ?? null })),
      },
    });
    return handleResponse<ProviderOrder>(res);
  },

  // TASK-241 (G3): upload an order-result photo (JPEG/PNG/WebP, ≤10MB) and
  // get back a hosted /media/… URL for attachProviderOrderResult.
  async uploadOrderResultPhoto(file: File): Promise<MediaUploadResponse> {
    const formData = new FormData();
    formData.append('file', file);
    const res = await request('/media/order-results', {
      method: 'POST',
      body: formData,
    });
    return handleResponse<MediaUploadResponse>(res);
  },

  // ME-1/ME-3: universal engine upload — kind derives from the extension
  // (images/video verified by content). Galleries and logos attach the
  // returned URL/thumbnail to their own entities.
  async uploadMedia(file: File): Promise<UploadedMedia> {
    const formData = new FormData();
    formData.append('file', file);
    const res = await request('/media/uploads', {
      method: 'POST',
      body: formData,
    });
    return handleResponse<UploadedMedia>(res);
  },

  // ── Admin panel (TASK-178) ─────────────────────────────

  async getAdminUsers(): Promise<AdminUser[]> {
    const res = await request('/admin/users');
    return handleResponse<AdminUser[]>(res);
  },

  /** 423ee061: paged user directory — email/name search + role filter. */
  async getAdminUsersPage(q: string, role: string, offset: number, limit: number): Promise<AdminUserPage> {
    const qs = new URLSearchParams({ offset: String(offset), limit: String(limit) });
    if (q) qs.set('q', q);
    if (role) qs.set('role', role);
    const res = await request(`/admin/users/page?${qs}`);
    return handleResponse<AdminUserPage>(res);
  },

  async setAdminUserRole(userId: string, role: string): Promise<AdminUser> {
    const res = await request(`/admin/users/${userId}/role`, {
      method: 'PUT',
      json: { role },
    });
    return handleResponse<AdminUser>(res);
  },

  async getAdminSettings(): Promise<PlatformSettings> {
    const res = await request('/admin/settings');
    return handleResponse<PlatformSettings>(res);
  },

  async saveAdminSettings(settings: PlatformSettings): Promise<PlatformSettings> {
    const res = await request('/admin/settings', {
      method: 'PUT',
      json: settings,
    });
    return handleResponse<PlatformSettings>(res);
  },

  // 1c936a07: marketplace commission hub — provider overrides list + edit.
  async getProviderCommissions(): Promise<ProviderCommission[]> {
    const res = await request('/admin/providers/commissions');
    return handleResponse<ProviderCommission[]>(res);
  },

  async setProviderCommissionRate(userId: string, percent: number | null): Promise<ProviderCommission> {
    const res = await request(`/admin/providers/${userId}/commission-rate`, {
      method: 'PUT',
      json: { commissionRatePercent: percent },
    });
    return handleResponse<ProviderCommission>(res);
  },

  // AS-1 (TASK-6e4bd7b5): read-only payments/notification environment status
  // for the settings hub (effective Safe Deal values + masked secrets).
  async getAdminPaymentsConfig(): Promise<PaymentsConfigStatus> {
    const res = await request('/admin/payments-config');
    return handleResponse<PaymentsConfigStatus>(res);
  },

  // ── TASK-245: admin order SLA visibility ──────────

  async getAdminOrders(): Promise<AdminOrder[]> {
    const res = await request('/admin/orders');
    return handleResponse<AdminOrder[]>(res);
  },

  /** 423ee061: paged admin order cabinet — status/date-range/counterparty
   * filters; 802d765a: + delivery-method filter, sort key (createdAt/status/total)
   * and direction (default desc = newest first), q now also matches the order
   * number (id prefix). */
  async getAdminOrdersPage(params: {
    status?: string;
    deliveryMethod?: string;
    fromUtc?: string;
    toUtc?: string;
    q?: string;
    sort?: string;
    dir?: 'asc' | 'desc';
    offset: number;
    limit: number;
  }): Promise<AdminOrderPage> {
    const qs = new URLSearchParams({ offset: String(params.offset), limit: String(params.limit) });
    if (params.status) qs.set('status', params.status);
    if (params.deliveryMethod) qs.set('deliveryMethod', params.deliveryMethod);
    if (params.fromUtc) qs.set('fromUtc', params.fromUtc);
    if (params.toUtc) qs.set('toUtc', params.toUtc);
    if (params.q) qs.set('q', params.q);
    if (params.sort) qs.set('sort', params.sort);
    if (params.dir) qs.set('dir', params.dir);
    const res = await request(`/admin/orders/page?${qs}`);
    return handleResponse<AdminOrderPage>(res);
  },

  // ── TASK-242: admin dispute resolution ────────────

  async getAdminDisputes(): Promise<AdminDispute[]> {
    const res = await request('/admin/disputes');
    return handleResponse<AdminDispute[]>(res);
  },

  /** 423ee061: paged dispute tab — longest-open first. */
  async getAdminDisputesPage(offset: number, limit: number): Promise<AdminDisputePage> {
    const qs = new URLSearchParams({ offset: String(offset), limit: String(limit) });
    const res = await request(`/admin/disputes/page?${qs}`);
    return handleResponse<AdminDisputePage>(res);
  },

  async resolveAdminDispute(orderId: string, outcome: DisputeOutcome, note?: string): Promise<AdminDispute> {
    const res = await request(`/admin/disputes/${orderId}/resolve`, {
      method: 'POST',
      json: { outcome, note },
    });
    return handleResponse<AdminDispute>(res);
  },

  // ── Seller pricing (TASK-179) ─────────────────────

  async getPricingSettings(): Promise<ProviderPricingSettings> {
    const res = await request('/providers/pricing/settings');
    return handleResponse<ProviderPricingSettings>(res);
  },

  async savePricingSettings(settings: ProviderPricingSettings): Promise<ProviderPricingSettings> {
    const res = await request('/providers/pricing/settings', {
      method: 'PUT',
      json: settings,
    });
    return handleResponse<ProviderPricingSettings>(res);
  },

  async previewSellerQuote(preview: SellerQuotePreviewRequest): Promise<SellerQuotePreview> {
    const res = await request('/providers/pricing/preview', {
      method: 'POST',
      json: preview,
    });
    return handleResponse<SellerQuotePreview>(res);
  },

  // ── Provider reviews (TASK-179) ─────────────────────

  async getProviderReviews(providerId: string): Promise<Review[]> {
    const res = await request(`/reviews/providers/${providerId}`);
    return handleResponse<Review[]>(res);
  },

  /** 054819ad: published product reviews (display-only v1, aggregate + items). */
  async getProductReviews(productId: string): Promise<ProductReviewsPage> {
    const res = await request(`/reviews/products/${productId}`);
    return handleResponse<ProductReviewsPage>(res);
  },

  /** 0aa086dd (audit-13 Card A): leave a product review — server gates on
   *  the customer's Completed order containing the product, one per product
   *  per order; errors carry the server's own gate message. */
  async createProductReview(orderId: string, productId: string, rating: number, text: string): Promise<ProductReview> {
    const res = await request('/reviews/products', {
      method: 'POST',
      json: { orderId, productId, rating, text },
    });
    return handleResponse<ProductReview>(res);
  },

  async createReview(orderId: string, rating: number, comment: string): Promise<Review> {
    const res = await request('/reviews', {
      method: 'POST',
      json: { orderId, rating, comment },
    });
    return handleResponse<Review>(res);
  },

  async reportReview(reviewId: string, reason: string): Promise<void> {
    const res = await request(`/reviews/${reviewId}/reports`, {
      method: 'POST',
      json: { reason },
    });
    await handleResponse<unknown>(res);
  },

  // ── Referrals (2f9c70ba) ───────────────────────────

  /** Public landing resolve by ?ref= code or vanity slug (counts a visit). */
  async resolveReferral(code: string): Promise<ReferralResolve> {
    const res = await request(`/referrals/resolve?code=${encodeURIComponent(code)}`);
    return handleResponse<ReferralResolve>(res);
  },

  /** Provider cabinet referral card (lazily provisioned code). */
  async getProviderReferral(): Promise<ProviderReferral> {
    const res = await request('/providers/me/referral');
    return handleResponse<ProviderReferral>(res);
  },

  /** Reserve (or release with an empty value) the vanity shop slug. */
  async setShopSlug(slug: string): Promise<ProviderReferral> {
    const res = await request('/providers/me/referral/slug', {
      method: 'PUT',
      json: { slug },
    });
    return handleResponse<ProviderReferral>(res);
  },

  /** Rotate the referral code. */
  async regenerateReferralCode(): Promise<ProviderReferral> {
    const res = await request('/providers/me/referral/code/regenerate', { method: 'POST' });
    return handleResponse<ProviderReferral>(res);
  },

  /** Admin: referral program settings (singleton row). */
  async getReferralSettings(): Promise<ReferralProgramSettings> {
    const res = await request('/admin/referrals/settings');
    return handleResponse<ReferralProgramSettings>(res);
  },

  async saveReferralSettings(settings: ReferralProgramSettings): Promise<ReferralProgramSettings> {
    const res = await request('/admin/referrals/settings', {
      method: 'PUT',
      json: settings,
    });
    return handleResponse<ReferralProgramSettings>(res);
  },

  async getReferralLedger(limit = 50): Promise<ReferralLedgerEntry[]> {
    const res = await request(`/admin/referrals/ledger?limit=${limit}`);
    return handleResponse<ReferralLedgerEntry[]>(res);
  },

  async getReferralAttributions(limit = 50): Promise<ReferralAttributionRow[]> {
    const res = await request(`/admin/referrals/attributions?limit=${limit}`);
    return handleResponse<ReferralAttributionRow[]>(res);
  },

  /* dfb793af (1cf7d337 part 3): Bambu Lab cabinet — account connect,
     connected card, disconnect and the bound-printer list. All
     endpoints are token-free by backend contract. */

  async getBambuConnection(): Promise<BambuConnection | null> {
    const res = await request('/providers/bambu/connection');
    return handleResponse<BambuConnection | null>(res);
  },

  async connectBambu(account: string, password: string): Promise<BambuConnection> {
    const res = await request('/providers/bambu/connect', {
      method: 'POST',
      json: { account, password },
    });
    return handleResponse<BambuConnection>(res);
  },

  async disconnectBambu(): Promise<BambuConnection> {
    const res = await request('/providers/bambu/disconnect', { method: 'POST' });
    return handleResponse<BambuConnection>(res);
  },

  /* a958628d: complete a 2FA login with the emailed 6-digit code;
     the challenge handle never leaves the server. */
  async verifyBambu(code: string): Promise<BambuConnection> {
    const res = await request('/providers/bambu/verify', {
      method: 'POST',
      json: { code },
    });
    return handleResponse<BambuConnection>(res);
  },

  async getBambuPrinters(): Promise<BambuPrinter[]> {
    const res = await request('/providers/bambu/printers');
    return handleResponse<BambuPrinter[]>(res);
  },

  /* 511c826a (c3858b9c part 2): Bambu send-to-print + control on the
     provider order page. All endpoints are access-code-free by
     backend contract. */

  async listOrderPrintJobs(orderId: string): Promise<BambuPrintJob[]> {
    const res = await request(`/printing/orders/${orderId}/print-jobs`);
    return handleResponse<BambuPrintJob[]>(res);
  },

  async createOrderPrintJob(orderId: string, printerId: string, slicingJobId: string): Promise<BambuPrintJob> {
    const res = await request(`/printing/orders/${orderId}/print-jobs`, {
      method: 'POST',
      json: { printerId, slicingJobId },
    });
    return handleResponse<BambuPrintJob>(res);
  },

  async controlOrderPrintJob(jobId: string, command: 'pause' | 'resume' | 'stop'): Promise<BambuPrintJob> {
    const res = await request(`/printing/print-jobs/${jobId}/control`, {
      method: 'POST',
      json: { command },
    });
    return handleResponse<BambuPrintJob>(res);
  },

  async refreshOrderPrintJob(jobId: string): Promise<BambuPrintJob> {
    const res = await request(`/printing/print-jobs/${jobId}/refresh`, { method: 'POST' });
    return handleResponse<BambuPrintJob>(res);
  },
};
